import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiSave, FiUser, FiMail, FiHome, FiBriefcase } from 'react-icons/fi';
import { Customer } from '../../../hooks/useCustomers';

interface CustomerFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (customer: Partial<Customer>) => void;
  customer?: Customer | null;
  loading?: boolean;
  error?: string;
  onDelete?: (customer: Customer) => void;
}

export const CustomerFormDrawer: React.FC<CustomerFormDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  customer,
  loading = false,
  error,
  onDelete,
}) => {
  const [formData, setFormData] = useState<Partial<Customer>>({
    first_name: '',
    last_name: '',
    company_name: '',
    display_name: '',
    email: '',
    phone: '',
    billing_addr_line1: '',
    billing_addr_city: '',
    billing_addr_state: '',
    billing_addr_zip: '',
    notes: '',
    tax_exempt: false,
    starting_balance: 0,
    shipping_same_as_billing: true,
    shipping_addr_line1: '',
    shipping_addr_city: '',
    shipping_addr_state: '',
    shipping_addr_zip: '',
    shipping_addr_country: ''
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const drawerRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Reset form when customer changes
  useEffect(() => {
    if (customer) {
      setFormData({
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        company_name: customer.company_name || '',
        display_name: customer.display_name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        billing_addr_line1: customer.billing_addr_line1 || '',
        billing_addr_city: customer.billing_addr_city || '',
        billing_addr_state: customer.billing_addr_state || '',
        billing_addr_zip: customer.billing_addr_zip || '',
        notes: customer.notes || '',
        tax_exempt: customer.tax_exempt || false,
        starting_balance: typeof customer.starting_balance === 'number' ? customer.starting_balance : 0,
        shipping_same_as_billing: customer.shipping_same_as_billing ?? true,
        shipping_addr_line1: customer.shipping_addr_line1 || '',
        shipping_addr_city: customer.shipping_addr_city || '',
        shipping_addr_state: customer.shipping_addr_state || '',
        shipping_addr_zip: customer.shipping_addr_zip || '',
        shipping_addr_country: (customer as any).shipping_addr_country || ''
      });
    } else {
      setFormData({
        first_name: '',
        last_name: '',
        company_name: '',
        display_name: '',
        email: '',
        phone: '',
        billing_addr_line1: '',
        billing_addr_city: '',
        billing_addr_state: '',
        billing_addr_zip: '',
        notes: '',
        tax_exempt: false,
        starting_balance: 0,
        shipping_same_as_billing: true,
        shipping_addr_line1: '',
        shipping_addr_city: '',
        shipping_addr_state: '',
        shipping_addr_zip: '',
        shipping_addr_country: ''
      });
    }
    setValidationErrors({});
  }, [customer]);

  // Focus management
  useEffect(() => {
    if (isOpen) {
      // Focus first input when drawer opens
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Focus trap for keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }

      // Focus trap - keep focus within drawer
      if (e.key === 'Tab') {
        const focusableElements = drawerRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        
        if (!focusableElements) return;
        
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleInputChange = (field: keyof Customer, value: string | boolean | number) => {
    setFormData(prev => ({ ...prev, [field]: value as any }));
    
    // Clear validation error for this field
    if ((validationErrors as any)[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.display_name?.trim()) {
      errors.display_name = 'Display name is required';
    }

    if (!formData.billing_addr_line1?.trim()) {
      errors.billing_addr_line1 = 'Street address is required';
    }

    if (!formData.billing_addr_city?.trim()) {
      errors.billing_addr_city = 'City is required';
    }

    if (!formData.billing_addr_state?.trim()) {
      errors.billing_addr_state = 'State is required';
    }

    if (!formData.billing_addr_zip?.trim()) {
      errors.billing_addr_zip = 'ZIP code is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (formData.phone && !/^[+]?\d[\d\s\-()]{5,}$/.test((formData.phone as string).replace(/[\s\-()]/g, ''))) {
      errors.phone = 'Please enter a valid phone number';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleDelete = async () => {
    if (!customer || !onDelete) return;
    const confirmed = window.confirm('Delete customer? This action is not reversible.');
    if (confirmed) {
      onDelete(customer);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`fixed top-0 right-0 h-full w-full sm:w-[680px] md:w-[720px] lg:w-[720px] max-w-[760px] bg-white dark:bg-neutral-900 shadow-xl transform transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
        aria-describedby="drawer-description"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-900/80 backdrop-blur">
          <h2 
            id="drawer-title"
            className="text-xl font-extrabold tracking-tight text-neutral-900 dark:text-neutral-50"
          >
            {customer ? 'Edit Customer' : 'Add New Customer'}
          </h2>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-200"
            aria-label="Close customer form"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div 
            className="mx-6 mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
            role="alert"
            aria-live="polite"
          >
            <p>{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="h-full flex flex-col">
          <div className="flex-1 overflow-y-auto p-6 pb-28 space-y-6">
            <p id="drawer-description" className="sr-only">
              {customer ? 'Edit customer information' : 'Add a new customer to your system'}
            </p>

            {/* Personal Information */}
            <fieldset>
              <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center">
                <FiUser className="mr-2" aria-hidden="true" />
                Personal Information
              </legend>
              <div className="rounded-2xl border border-gray-200 bg-white p-5 elev-1 dark:bg-neutral-900 dark:border-neutral-800">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="first_name" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      First Name
                    </label>
                    <input
                      ref={firstInputRef}
                      id="first_name"
                      name="first_name"
                      type="text"
                      value={formData.first_name || ''}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label htmlFor="last_name" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Last Name
                    </label>
                    <input
                      id="last_name"
                      name="last_name"
                      type="text"
                      value={formData.last_name || ''}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            </fieldset>

            {/* Company Information */}
            <fieldset>
              <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center">
                <FiBriefcase className="mr-2" aria-hidden="true" />
                Company Information
              </legend>
              <div className="rounded-2xl border border-gray-200 bg-white p-5 elev-1 dark:bg-neutral-900 dark:border-neutral-800">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="company_name" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Company Name
                    </label>
                    <input
                      id="company_name"
                      name="company_name"
                      type="text"
                      value={formData.company_name || ''}
                      onChange={(e) => handleInputChange('company_name', e.target.value)}
                      className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label htmlFor="display_name" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Display Name *
                    </label>
                    <input
                      id="display_name"
                      name="display_name"
                      type="text"
                      value={formData.display_name || ''}
                      onChange={(e) => handleInputChange('display_name', e.target.value)}
                      placeholder="Enter display name"
                      className={`mt-1 block w-full rounded-xl border bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:text-white ${
                        validationErrors.display_name 
                          ? 'border-red-500' 
                          : 'border-gray-300 dark:border-neutral-700'
                      }`}
                      required
                      aria-invalid={!!validationErrors.display_name}
                      aria-describedby={validationErrors.display_name ? 'display_name-error' : undefined}
                    />
                    {validationErrors.display_name && (
                      <p id="display_name-error" className="text-red-500 text-xs mt-1" role="alert">
                        {validationErrors.display_name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </fieldset>

            {/* Contact Information */}
            <fieldset>
              <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center">
                <FiMail className="mr-2" aria-hidden="true" />
                Contact Information
              </legend>
              <div className="rounded-2xl border border-gray-200 bg-white p-5 elev-1 dark:bg-neutral-900 dark:border-neutral-800">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`mt-1 block w-full rounded-xl border bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:text-white ${
                        validationErrors.email 
                          ? 'border-red-500' 
                          : 'border-gray-300 dark:border-neutral-700'
                      }`}
                      aria-invalid={!!validationErrors.email}
                      aria-describedby={validationErrors.email ? 'email-error' : undefined}
                    />
                    {validationErrors.email && (
                      <p id="email-error" className="text-red-500 text-xs mt-1" role="alert">
                        {validationErrors.email}
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Phone
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone || ''}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className={`mt-1 block w-full rounded-xl border bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:text-white ${
                        validationErrors.phone 
                          ? 'border-red-500' 
                          : 'border-gray-300 dark:border-neutral-700'
                      }`}
                      aria-invalid={!!validationErrors.phone}
                      aria-describedby={validationErrors.phone ? 'phone-error' : undefined}
                    />
                    {validationErrors.phone && (
                      <p id="phone-error" className="text-red-500 text-xs mt-1" role="alert">
                        {validationErrors.phone}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </fieldset>

            {/* Billing Address */}
            <fieldset>
              <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center">
                <FiHome className="mr-2" aria-hidden="true" />
                Billing Address
              </legend>
              <div className="rounded-2xl border border-gray-200 bg-white p-5 elev-1 dark:bg-neutral-900 dark:border-neutral-800">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="billing_addr_line1" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Street Address *
                    </label>
                    <input
                      id="billing_addr_line1"
                      name="billing_addr_line1"
                      type="text"
                      value={formData.billing_addr_line1 || ''}
                      onChange={(e) => handleInputChange('billing_addr_line1', e.target.value)}
                      className={`mt-1 block w-full rounded-xl border bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:text-white ${
                        validationErrors.billing_addr_line1 
                          ? 'border-red-500' 
                          : 'border-gray-300 dark:border-neutral-700'
                      }`}
                      required
                      aria-invalid={!!validationErrors.billing_addr_line1}
                      aria-describedby={validationErrors.billing_addr_line1 ? 'billing_addr_line1-error' : undefined}
                    />
                    {validationErrors.billing_addr_line1 && (
                      <p id="billing_addr_line1-error" className="text-red-500 text-xs mt-1" role="alert">
                        {validationErrors.billing_addr_line1}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="billing_addr_city" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                        City *
                      </label>
                      <input
                        id="billing_addr_city"
                        name="billing_addr_city"
                        type="text"
                        value={formData.billing_addr_city || ''}
                        onChange={(e) => handleInputChange('billing_addr_city', e.target.value)}
                        className={`mt-1 block w-full rounded-xl border bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:text-white ${
                          validationErrors.billing_addr_city 
                            ? 'border-red-500' 
                            : 'border-gray-300 dark:border-neutral-700'
                        }`}
                        required
                        aria-invalid={!!validationErrors.billing_addr_city}
                        aria-describedby={validationErrors.billing_addr_city ? 'billing_addr_city-error' : undefined}
                      />
                      {validationErrors.billing_addr_city && (
                        <p id="billing_addr_city-error" className="text-red-500 text-xs mt-1" role="alert">
                          {validationErrors.billing_addr_city}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="billing_addr_state" className="block text sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                        State *
                      </label>
                      <input
                        id="billing_addr_state"
                        name="billing_addr_state"
                        type="text"
                        value={formData.billing_addr_state || ''}
                        onChange={(e) => handleInputChange('billing_addr_state', e.target.value)}
                        className={`mt-1 block w-full rounded-xl border bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:text-white ${
                          validationErrors.billing_addr_state 
                            ? 'border-red-500' 
                            : 'border-gray-300 dark:border-neutral-700'
                        }`}
                        required
                        aria-invalid={!!validationErrors.billing_addr_state}
                        aria-describedby={validationErrors.billing_addr_state ? 'billing_addr_state-error' : undefined}
                      />
                      {validationErrors.billing_addr_state && (
                        <p id="billing_addr_state-error" className="text-red-500 text-xs mt-1" role="alert">
                          {validationErrors.billing_addr_state}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="billing_addr_zip" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                        ZIP Code *
                      </label>
                      <input
                        id="billing_addr_zip"
                        name="billing_addr_zip"
                        type="text"
                        value={formData.billing_addr_zip || ''}
                        onChange={(e) => handleInputChange('billing_addr_zip', e.target.value)}
                        className={`mt-1 block w-full rounded-xl border bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:text-white ${
                          validationErrors.billing_addr_zip 
                            ? 'border-red-500' 
                            : 'border-gray-300 dark:border-neutral-700'
                        }`}
                        required
                        aria-invalid={!!validationErrors.billing_addr_zip}
                        aria-describedby={validationErrors.billing_addr_zip ? 'billing_addr_zip-error' : undefined}
                      />
                      {validationErrors.billing_addr_zip && (
                        <p id="billing_addr_zip-error" className="text-red-500 text-xs mt-1" role="alert">
                          {validationErrors.billing_addr_zip}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </fieldset>

            {/* Shipping Address */}
            <fieldset>
              <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center">
                <FiHome className="mr-2" aria-hidden="true" />
                Shipping Address
              </legend>
              <div className="rounded-2xl border border-gray-200 bg-white p-5 elev-1 dark:bg-neutral-900 dark:border-neutral-800">
                <div className="space-y-4">
                  <label className="inline-flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={!!formData.shipping_same_as_billing}
                      onChange={(e) => handleInputChange('shipping_same_as_billing', e.target.checked)}
                    />
                    <span>Same as billing</span>
                  </label>

                  {!formData.shipping_same_as_billing && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                          Street
                        </label>
                        <input
                          type="text"
                          value={formData.shipping_addr_line1 || ''}
                          onChange={(e) => handleInputChange('shipping_addr_line1', e.target.value)}
                          className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">City</label>
                          <input
                            type="text"
                            value={formData.shipping_addr_city || ''}
                            onChange={(e) => handleInputChange('shipping_addr_city', e.target.value)}
                            className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">State</label>
                          <input
                            type="text"
                            value={formData.shipping_addr_state || ''}
                            onChange={(e) => handleInputChange('shipping_addr_state', e.target.value)}
                            className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">ZIP</label>
                          <input
                            type="text"
                            value={formData.shipping_addr_zip || ''}
                            onChange={(e) => handleInputChange('shipping_addr_zip', e.target.value)}
                            className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Country</label>
                        <input
                          type="text"
                          value={formData.shipping_addr_country || ''}
                          onChange={(e) => handleInputChange('shipping_addr_country', e.target.value)}
                          className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </fieldset>

            {/* Financials */}
            <fieldset>
              <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center">
                Financials
              </legend>
              <div className="rounded-2xl border border-gray-200 bg-white p-5 elev-1 dark:bg-neutral-900 dark:border-neutral-800">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="starting_balance" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Starting Balance
                    </label>
                    <input
                      id="starting_balance"
                      name="starting_balance"
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={
                        typeof formData.starting_balance === 'number'
                          ? formData.starting_balance.toString()
                          : (formData.starting_balance as any) || ''
                      }
                      onChange={(e) => handleInputChange('starting_balance', e.target.value)}
                      className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                    />
                    <p className="text-xs text-neutral-500 mt-1">
                      Optional. Use up to 2 decimals. Positive amounts show as overdue; negatives are treated as credit.
                    </p>
                  </div>
                </div>
              </div>
            </fieldset>

            {/* Additional Information */}
            <fieldset>
              <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                Additional Information
              </legend>
              <div className="rounded-2xl border border-gray-200 bg-white p-5 elev-1 dark:bg-neutral-900 dark:border-neutral-800">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Notes
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes || ''}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      rows={3}
                      className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                      placeholder="Any additional notes about this customer"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      id="tax_exempt"
                      name="tax_exempt"
                      type="checkbox"
                      checked={formData.tax_exempt || false}
                      onChange={(e) => handleInputChange('tax_exempt', e.target.checked)}
                      className="h-4 w-4 text-brand focus:ring-brand border-neutral-300 rounded"
                    />
                    <label htmlFor="tax_exempt" className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300">
                      Tax Exempt
                    </label>
                  </div>
                </div>
              </div>
            </fieldset>
          </div>

          {/* Sticky Footer */}
          <div className="sticky bottom-0 left-0 right-0 bg-white dark:bg-neutral-900 p-6 border-t border-neutral-200 dark:border-neutral-800 z-10">
            <div className="flex justify-between items-center">
              {/* Left side: destructive action when editing */}
              <div>
                {customer && onDelete && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="rounded-xl border border-red-200 text-red-700 bg-red-50 px-4 py-2.5 text-sm font-semibold hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-500 dark:border-red-800 dark:text-red-300 dark:bg-red-900/20"
                  >
                    Delete Customer
                  </button>
                )}
              </div>
              {/* Right side: primary actions */}
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sedra-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  <FiSave className="mr-2 h-4 w-4" />
                  {customer ? 'Update Customer' : 'Add Customer'}
                </button>
              </div>
            </div>
            {loading && (
              <p id="loading-status" className="sr-only" aria-live="polite">
                Saving customer information...
              </p>
            )}
          </div>
        </form>
      </div>
    </>
  );
}; 